import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { forwardRef } from 'react';

import { cn } from './cn';
import { DialogOverlay } from './Dialog';
import { useReturnFocus } from './useReturnFocus';

export const Sheet = DialogPrimitive.Root;
export const SheetTrigger = DialogPrimitive.Trigger;
export const SheetClose = DialogPrimitive.Close;
export const SheetTitle = DialogPrimitive.Title;
export const SheetDescription = DialogPrimitive.Description;

export interface SheetContentProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  side?: 'left' | 'right';
}

export const SheetContent = forwardRef<React.ElementRef<typeof DialogPrimitive.Content>, SheetContentProps>(
  ({ children, className, side = 'right', onOpenAutoFocus, onCloseAutoFocus, ...props }, ref) => {
    const {
      handlers: { onOpenAutoFocus: rememberOpener, onCloseAutoFocus: returnFocus },
      capture,
    } = useReturnFocus({
      onOpenAutoFocus,
      onCloseAutoFocus,
    });
    return (
      <DialogPrimitive.Portal>
        <DialogOverlay />
        <DialogPrimitive.Content
          ref={ref}
          onOpenAutoFocus={rememberOpener}
          onCloseAutoFocus={returnFocus}
          className={cn(
            'fixed inset-y-0 z-overlay flex max-w-full flex-col overflow-y-auto overscroll-contain border-line bg-surface text-ink',
            'shadow-auditor-overlay focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2',
            side === 'right' ? 'right-0 border-l' : 'left-0 border-r',
            className,
          )}
          {...props}
        >
          {capture}
          {children}
          <DialogPrimitive.Close
            className={cn(
              'absolute right-4 top-4 rounded-auditor p-1 text-ink-muted transition-colors hover:bg-canvas hover:text-ink',
              'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand',
            )}
          >
            <X aria-hidden="true" className="size-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    );
  },
);
SheetContent.displayName = 'SheetContent';
